import React, {  useState, useContext } from "react";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import { login } from "../../../service/ApiServices";
import { notification } from "antd"; // Using Ant Design notification
import { UserContext } from "../../../context/userContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { getUserById } from "../../../service/userService";
import { LoadingContext } from "../../../context/loadingContext";

const Signin = () => {
  localStorage.clear();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { setUser } = useContext(UserContext);
  const { setLoading1 } = useContext(LoadingContext);
  const navigate = useNavigate();
  const route = all_routes;

  const [api, contextHolder] = notification.useNotification(); // Ant Design notification hook

  const openNotification = (message, description) => {
    api.error({
      message,
      description,
      placement: "topRight",
      duration: 6, // Auto-close after 6 seconds
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading1(true);

    if (email === "") {
      openNotification("Input Error", "EPF number cannot be empty.");
      setLoading1(false);
      return;
    }

    if (password === "") {
      openNotification("Input Error", "Password cannot be empty.");
      setLoading1(false);
      return;
    }

    try {
      const res = await login({
        email: email,
        password: password,
      });

      if (res.data.status === 200) {
        localStorage.setItem("user", JSON.stringify(res.data));
        const userData = res.data.data;
        await fetchUserData(userData);

        const permissions = JSON.parse(localStorage.getItem("permissions"));
        const dashboardRoute = determineAccessibleDashboard(permissions);
        navigate(dashboardRoute);
        setLoading1(false);
      } else {
        setLoading1(false);
        openNotification("Login Failed", "Invalid EPF number or password. Please try again.");
      }
    } catch (error) {
      setLoading1(false);
      const errorMessage = error.response?.data?.message || "An unexpected error occurred. Please contact support for assistance.";
      openNotification("Login Error", "Invalid EPF number or password. Please try again." ,errorMessage);
    }
  };

  const determineAccessibleDashboard = (permissions) => {
    if (!permissions || permissions.length === 0) {
      console.log("No permissions, defaulting to /employee-dashboard");
      return "/employee-dashboard";
    }

    const isAdmin = permissions.some((perm) =>
      perm.label === "Dashboard" &&
      perm.functions?.some((func) => func.label === "Admin Dashboard")
    );

    if (isAdmin) {
      return "/";
    }

    return "/employee-dashboard";
  };

  const fetchUserData = async (userObj) => {
    try {
      const response = await getUserById(userObj._id);
      const { data } = response;

      const [firstName, ...lastNameParts] = data.name.split(" ");
      const lastName = lastNameParts.join(" ");

      const permissions =
        data.role?.modules?.map((module) => {
          let moduleLabel = "Unknown Module";
          if (module.moduleId) {
            moduleLabel = typeof module.moduleId === "object" ? module.moduleId.label : "Module ID: " + module.moduleId;
          }

          return {
            moduleId: module.moduleId?._id || module.moduleId,
            label: moduleLabel,
            functions:
              module.functions?.map((func) => {
                let functionLabel = "Unknown Function";
                if (func.functionId) {
                  functionLabel =
                    typeof func.functionId === "object" ? func.functionId.label : "Function ID: " + func.functionId;
                }

                return {
                  functionId: func.functionId?._id || func.functionId,
                  label: functionLabel,
                  permissions: func.permissions || {},
                  subFunctions:
                    func.subFunctions?.map((subFunc) => {
                      let subFunctionLabel = "Unknown Subfunction";
                      if (subFunc.functionId) {
                        subFunctionLabel =
                          typeof subFunc.functionId === "object"
                            ? subFunc.functionId.label
                            : "Subfunction ID: " + subFunc.functionId;
                      }

                      return {
                        functionId: subFunc.functionId?._id || func.functionId,
                        label: subFunctionLabel,
                        permissions: subFunc.permissions || {},
                      };
                    }) || [],
                };
              }) || [],
          };
        }) || [];

      localStorage.setItem("permissions", JSON.stringify(permissions));

      setUser({
        firstName,
        lastName,
        profile_pic: data.profile_picture,
        role: data.role?.roleName,
        permissions,
        isAuthenticated: true,
      });
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      openNotification("Data Fetch Error", "Failed to retrieve user details. Please contact support for assistance.");
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="main-wrapper">
      {contextHolder} {/* Ant Design notification context */}
      <div className="account-content">
        <div className="login-wrapper bg-img">
          <div className="login-content">
            <form onSubmit={handleLogin}>
              <div className="login-userset">
                <div className="login-logo logo-normal">
                  <ImageWithBasePath src="assets/img/OKI-logo.png" alt="img" />
                </div>
                <Link to={route.dashboard} className="login-logo logo-white">
                  <ImageWithBasePath src="assets/img/logo-white.png" alt="" />
                </Link>
                <div className="login-userheading">
                  <h3>Sign In</h3>
                  <h4>Access the suit panel using your EPF No and password.</h4>
                </div>
                <div className="form-login mb-3">
                  <label className="form-label">EPF No</label>
                  <div className="form-addons">
                    <input
                      type="text"
                      className="form-control"
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="1234"
                    />
                    <ImageWithBasePath src="assets/img/icons/user-icon.svg" alt="img" />
                  </div>
                </div>
                <div className="form-login mb-3">
                  <label className="form-label">Password</label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="pass-input form-control"
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Abc12345@"
                    />
                    <span
                      className="fas toggle-password"
                      onClick={handleTogglePassword}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                    </span>
                  </div>
                </div>
                <div className="form-login authentication-check">
                  <div className="row">
                    <div className="col-12 d-flex align-items-center justify-content-between" >
                      <div>
                        &nbsp;
                      </div>
                      <div className="text-end">
                        <Link className="forgot-link" to={route.forgotPassword}>
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                    <div className="row">
                      <div className="custom-control custom-checkbox">
                          <label className="checkboxs ps-4 mb-0 pb-0 line-height-1">
                            <input type="checkbox" className="form-control" />
                            <span className="checkmarks" />
                            Remember me
                          </label>
                        </div>
                    </div>
                  </div>
                </div>
                <div className="form-login">
                  <button type="submit" className="btn btn-login">
                    Sign In
                  </button>
                </div>
                <div className="form-sociallink">
                  <div className="my-4 d-flex justify-content-center align-items-center copyright-text">
                    <p>Copyright © 2025 Eyerax Technologies. All rights reserved</p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signin;